import { useEffect, useMemo, useState } from 'react';
import type { SortingState } from '@tanstack/react-table';
import { useClientQueries } from '@zenstackhq/tanstack-query/react';
import { schema } from '../__generated__/schema-lite';

type ModelName = keyof typeof schema.models;

type Opts = {
  where?: Record<string, unknown>;
  include?: Record<string, unknown>;
  select?: Record<string, unknown>;
  pageSize?: number;
  initialSorting?: SortingState;
  /** Forwarded to the underlying query. `'always'` refetches on every mount
   *  (e.g. navigating back to the page) regardless of the global staleTime, so
   *  the list is never shown stale. */
  refetchOnMount?: boolean | 'always';
};

type FindManyHook = (
  args: unknown,
  options?: unknown,
) => {
  data?: unknown[];
  isLoading: boolean;
  error: unknown;
  refetch: () => Promise<unknown>;
};

/** Per-resource "ViewModel" — owns pagination + sorting state and runs `useFindMany`.
 *  Returns the shape that `ResourceTable` consumes. Compose multiple of these in a
 *  page-level VM when you need cross-table coordination. */
export function useResourceQuery<M extends ModelName>(modelName: M, opts: Opts = {}) {
  const pageSize = opts.pageSize ?? 25;
  const [sorting, setSorting] = useState<SortingState>(opts.initialSorting ?? []);
  const [pageIndex, setPageIndex] = useState(0);

  // Reset to first page whenever the filter changes — otherwise you can end up on
  // a page that no longer exists after switching selection upstream.
  const whereKey = JSON.stringify(opts.where ?? null);
  useEffect(() => {
    setPageIndex(0);
  }, [whereKey]);

  const orderBy = useMemo(() => {
    if (sorting.length === 0) return undefined;
    const s = sorting[0]!;
    return { [s.id]: s.desc ? 'desc' : 'asc' };
  }, [sorting]);

  const crud = useClientQueries(schema);
  const modelKey = (modelName as string).charAt(0).toLowerCase() + (modelName as string).slice(1);
  const findMany = (crud as unknown as Record<string, { useFindMany: FindManyHook }>)[modelKey]!
    .useFindMany;
  const { data, isLoading, error, refetch } = findMany(
    {
      take: pageSize,
      skip: pageIndex * pageSize,
      orderBy,
      where: opts.where,
      include: opts.include,
      select: opts.select,
    },
    opts.refetchOnMount !== undefined ? { refetchOnMount: opts.refetchOnMount } : undefined,
  );

  return {
    // `model` matches @diadraw/ui's ResourceTable prop so the query can be
    // spread straight in: <ResourceTable {...query} … />.
    model: modelName,
    rows: (data as Record<string, unknown>[] | undefined) ?? [],
    isLoading,
    error,
    sorting,
    onSortingChange: setSorting,
    pageIndex,
    pageSize,
    onPageIndexChange: setPageIndex,
    // Manual re-run of the query (e.g. a "Sync now" button).
    refetch,
  };
}

export type ResourceQuery<M extends ModelName = ModelName> = ReturnType<typeof useResourceQuery<M>>;
